import type {
	ActivityAction,
	ActivityCategory,
	ActivityDetails,
} from "../services/api";

export const ACTION_LABELS: Record<ActivityAction, string> = {
	"create-license": "Created license",
	"update-license": "Updated license",
	"renew-license": "Renewed license",
	"revoke-license": "Revoked license",
	"reuse-license": "Re-used license",
	"hold-license": "Put license on hold",
	"release-license": "Released license from hold",
	"delete-license": "Deleted license",
	"bulk-revoke-licenses": "Revoked licenses in bulk",
	"bulk-hold-licenses": "Put licenses on hold in bulk",
	"bulk-release-licenses": "Released licenses from hold in bulk",
	"bulk-renew-licenses": "Renewed licenses in bulk",
	"bulk-delete-licenses": "Deleted licenses in bulk",
	"bulk-set-license-type": "Set license type in bulk",
	"activate-station": "Activated station",
	"deactivate-station": "Deactivated station",
	"create-account": "Created account",
	"update-account": "Updated account",
	"delete-account": "Deleted account",
	"send-report": "Sent report",
	"remove-report-subscriber": "Removed report subscriber",
	"create-user": "Created user",
	"update-user": "Updated user",
	"delete-user": "Deleted user",
	login: "Signed in",
	"login-failed": "Failed sign-in",
	"change-password": "Changed password",
	"logout-all": "Signed out everywhere",
	"create-api-key": "Created API key",
	"activate-api-key": "Activated API key",
	"deactivate-api-key": "Deactivated API key",
};

export const CATEGORY_LABELS: Record<ActivityCategory, string> = {
	license: "Licenses",
	station: "Stations",
	account: "Accounts",
	user: "Users",
	auth: "Auth",
	report: "Reports",
};

// The backend can ship a new action before this build knows it; its raw key
// still says more than a blank cell
export const toActionLabel = (action: string): string =>
	ACTION_LABELS[action as ActivityAction] ?? action;

const RELATIVE_TIME_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
	["year", 365 * 24 * 60 * 60],
	["month", 30 * 24 * 60 * 60],
	["week", 7 * 24 * 60 * 60],
	["day", 24 * 60 * 60],
	["hour", 60 * 60],
	["minute", 60],
	["second", 1],
];

const relativeTimeFormat = new Intl.RelativeTimeFormat("en", {
	numeric: "auto",
});

export const toRelativeTime = (timestamp: string, now = Date.now()): string => {
	// A server clock a little ahead of this one would otherwise read "in 3 seconds"
	const secondsAgo = Math.max(
		0,
		Math.round((now - new Date(timestamp).getTime()) / 1000),
	);
	const [unit, unitSeconds] = RELATIVE_TIME_UNITS.find(
		([, seconds]) => secondsAgo >= seconds,
	) ?? ["second", 1];
	return relativeTimeFormat.format(-Math.floor(secondsAgo / unitSeconds), unit);
};

export const toExactTime = (timestamp: string): string =>
	new Date(timestamp).toLocaleString("he-IL");

const pluralize = (count: number, noun: string) =>
	`${count} ${noun}${count === 1 ? "" : "s"}`;

export const toDetailsSummary = (details?: ActivityDetails): string => {
	if (!details) return "";
	return [
		// Only bulk license actions carry a count
		details.count !== undefined && pluralize(details.count, "license"),
		details.changed?.length && `Changed ${details.changed.join(", ")}`,
		details.licenseType && `Type ${details.licenseType}`,
		details.expirationDate &&
			`Until ${new Date(details.expirationDate).toLocaleDateString("he-IL")}`,
		details.recipients !== undefined &&
			pluralize(details.recipients, "recipient"),
		details.clientUid && `Station ${details.clientUid}`,
	]
		.filter(Boolean)
		.join(" · ");
};
